/* assets/os-countdown.js — One Source cutoff countdown
   Ported from production Luxon-based timer. Uses America/Detroit timezone
   so every visitor sees the same countdown regardless of their local clock.

   Closed days = Saturday, Sunday, and any holiday enabled on the
   OS · Status Strip section (Theme editor → Holiday closures). The strip
   renders data-os-holidays="labor,thanksgiving,..." (rule keys, computed
   per year) and data-os-closed-dates="2026-12-28,2026-12-29" (one-offs).
   The timer always targets 12:00 PM ET on the next open day.
*/
(function(){
  var root = (typeof window !== 'undefined') ? window : globalThis;
  var doc  = (typeof document !== 'undefined') ? document : null;

  // Several sections include this asset; only the first copy should run.
  if(root.OSCountdown) return;

  // Load Luxon if not already present
  function loadLuxon(cb){
    if(root.luxon){ cb(); return; }
    var s = doc.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/luxon@3/build/global/luxon.min.js';
    s.onload = cb;
    doc.head.appendChild(s);
  }

  // ready(fn): run fn once Luxon is loaded (immediately if it already is).
  var readyQueue = [], isReady = false;
  function ready(fn){ if(isReady){ fn(); } else { readyQueue.push(fn); } }
  function flushReady(){ isReady = true; readyQueue.splice(0).forEach(function(fn){ try { fn(); } catch(e){ console.error(e); } }); }

  var ZONE = 'America/Detroit';
  var CUTOFF_HOUR = 12; // 12:00 PM ET

  function pad(n){ return String(n).padStart(2,'0'); }

  /* ---------------- Holiday rules ----------------
     Each rule returns the observed closure date for a given year as a
     Luxon DateTime (midnight, ZONE). Fixed-date holidays that land on a
     weekend shift to the nearest weekday (Sat->Fri, Sun->Mon) the way most
     US employers observe them. Eves (Dec 24 / Dec 31) do not shift. */
  function nthWeekday(DateTime, year, month, weekday, n){
    var first = DateTime.fromObject({ year: year, month: month, day: 1 }, { zone: ZONE });
    var offset = (weekday - first.weekday + 7) % 7;
    return first.plus({ days: offset + 7 * (n - 1) });
  }
  function lastWeekday(DateTime, year, month, weekday){
    var last = DateTime.fromObject({ year: year, month: month, day: 1 }, { zone: ZONE }).endOf('month').startOf('day');
    var offset = (last.weekday - weekday + 7) % 7;
    return last.minus({ days: offset });
  }
  function fixed(DateTime, year, month, day){
    return DateTime.fromObject({ year: year, month: month, day: day }, { zone: ZONE });
  }
  function observed(dt){
    if(dt.weekday === 6) return dt.minus({ days: 1 }); // Sat -> Fri
    if(dt.weekday === 7) return dt.plus({ days: 1 });  // Sun -> Mon
    return dt;
  }

  var RULES = {
    new_years:     function(D, y){ return observed(fixed(D, y, 1, 1)); },
    mlk:           function(D, y){ return nthWeekday(D, y, 1, 1, 3); },
    presidents:    function(D, y){ return nthWeekday(D, y, 2, 1, 3); },
    memorial:      function(D, y){ return lastWeekday(D, y, 5, 1); },
    juneteenth:    function(D, y){ return observed(fixed(D, y, 6, 19)); },
    independence:  function(D, y){ return observed(fixed(D, y, 7, 4)); },
    labor:         function(D, y){ return nthWeekday(D, y, 9, 1, 1); },
    thanksgiving:  function(D, y){ return nthWeekday(D, y, 11, 4, 4); },
    black_friday:  function(D, y){ return nthWeekday(D, y, 11, 4, 4).plus({ days: 1 }); },
    christmas_eve: function(D, y){ return fixed(D, y, 12, 24); },
    christmas:     function(D, y){ return observed(fixed(D, y, 12, 25)); },
    new_years_eve: function(D, y){ return fixed(D, y, 12, 31); }
  };

  /* ---------------- Closure config ----------------
     Read from every element in the page that carries the data attributes
     (normally just the status strip in the header group). Can also be set
     programmatically via OSCountdown.setClosures({holidays:[...], dates:[...]}). */
  var manual = null;
  function splitList(str){
    return String(str || '').split(/[\s,]+/).map(function(s){ return s.trim(); }).filter(Boolean);
  }
  function readClosures(){
    if(manual) return manual;
    var holidays = {}, dates = {};
    if(doc){
      doc.querySelectorAll('[data-os-holidays]').forEach(function(el){
        splitList(el.getAttribute('data-os-holidays')).forEach(function(k){ holidays[k] = true; });
      });
      doc.querySelectorAll('[data-os-closed-dates]').forEach(function(el){
        splitList(el.getAttribute('data-os-closed-dates')).forEach(function(d){
          if(/^\d{4}-\d{2}-\d{2}$/.test(d)) dates[d] = true;
        });
      });
    }
    return { holidays: Object.keys(holidays), dates: dates };
  }
  function setClosures(cfg){
    if(!cfg){ manual = null; return; }
    var dates = {};
    (cfg.dates || []).forEach(function(d){ dates[d] = true; });
    manual = { holidays: (cfg.holidays || []).slice(), dates: dates };
    holidayCache = {};
  }

  // Cache of "yyyy|key1,key2" -> { 'yyyy-MM-dd': true }
  var holidayCache = {};
  function holidayDates(DateTime, year, keys){
    var cacheKey = year + '|' + keys.slice().sort().join(',');
    if(holidayCache[cacheKey]) return holidayCache[cacheKey];
    var out = {};
    keys.forEach(function(k){
      var rule = RULES[k];
      if(rule) out[rule(DateTime, year).toISODate()] = true;
    });
    holidayCache[cacheKey] = out;
    return out;
  }

  function isHoliday(DateTime, dt){
    var cfg = readClosures();
    var iso = dt.toISODate();
    if(cfg.dates[iso]) return true;
    if(!cfg.holidays.length) return false;
    return !!holidayDates(DateTime, dt.year, cfg.holidays)[iso];
  }
  function isWeekend(dt){ return dt.weekday === 6 || dt.weekday === 7; }
  function isClosedDay(DateTime, dt){ return isWeekend(dt) || isHoliday(DateTime, dt); }

  // First open day strictly after `dt` (bounded so a bad config cannot spin)
  function nextOpenDay(DateTime, dt){
    var d = dt.plus({ days: 1 }).startOf('day');
    for(var i = 0; i < 30 && isClosedDay(DateTime, d); i++) d = d.plus({ days: 1 });
    return d;
  }

  function nextShippingCutoff(DateTime, now){
    var target = now.set({ hour: CUTOFF_HOUR, minute: 0, second: 0, millisecond: 0 });
    if(isClosedDay(DateTime, now) || now >= target){
      target = nextOpenDay(DateTime, now).set({ hour: CUTOFF_HOUR, minute: 0, second: 0, millisecond: 0 });
    }
    return target;
  }

  function stateForNow(now){
    var DateTime = root.luxon.DateTime;
    if(isWeekend(now)) return 'weekend';
    if(isHoliday(DateTime, now)) return 'holiday';
    var cutoff = now.set({ hour: CUTOFF_HOUR, minute: 0, second: 0, millisecond: 0 });
    return now < cutoff ? 'beforeCutoff' : 'afterCutoff';
  }

  // Stage color thresholds based on hours remaining
  function stageColor(h){
    if (h > 12) return '#40C4FF';  // cyan  — next-day
    if (h > 6)  return '#00C853';  // green — plenty of time
    if (h > 3)  return '#FFB300';  // amber — getting tight
    return '#FF1744';              // red   — final hours
  }

  function render(){
    var DateTime = root.luxon && root.luxon.DateTime;
    if(!DateTime || !doc) return;

    var now = DateTime.now().setZone(ZONE);
    var target = nextShippingCutoff(DateTime, now);
    var diff = target.diff(now, ['hours','minutes','seconds']).toObject();

    var h = Math.max(0, Math.floor(diff.hours || 0));
    var m = Math.max(0, Math.floor(diff.minutes || 0));
    var s = Math.max(0, Math.floor(diff.seconds || 0));

    doc.querySelectorAll('[data-os-cd-h]').forEach(function(el){ el.textContent = pad(h); });
    doc.querySelectorAll('[data-os-cd-m]').forEach(function(el){ el.textContent = pad(m); });
    doc.querySelectorAll('[data-os-cd-s]').forEach(function(el){ el.textContent = pad(s); });

    // Mode switching (before/after/weekend/holiday) for copy blocks.
    // Sections without a dedicated holiday block fall back to their weekend copy.
    var mode = stateForNow(now);
    if(mode === 'holiday' && !doc.querySelector('[data-os-mode="holiday"]')) mode = 'weekend';
    doc.querySelectorAll('[data-os-mode]').forEach(function(el){
      el.style.display = (el.dataset.osMode === mode) ? '' : 'none';
    });

    // Stage color on digit boxes (scoped via CSS var so themes can override)
    var color = stageColor(h);
    doc.querySelectorAll('.os-countdown-bar').forEach(function(bar){
      bar.style.setProperty('--os-cd-urgency', color);
    });
  }

  root.OSCountdown = {
    ZONE: ZONE,
    CUTOFF_HOUR: CUTOFF_HOUR,
    ready: ready,
    render: render,
    setClosures: setClosures,
    readClosures: readClosures,
    holidayDates: function(year, keys){ return holidayDates(root.luxon.DateTime, year, keys); },
    isHoliday:   function(dt){ return isHoliday(root.luxon.DateTime, dt); },
    isClosedDay: function(dt){ return isClosedDay(root.luxon.DateTime, dt); },
    nextOpenDay: function(dt){ return nextOpenDay(root.luxon.DateTime, dt); },
    nextShippingCutoff: function(now){ return nextShippingCutoff(root.luxon.DateTime, now); },
    stateForNow: stateForNow
  };

  if(doc){
    doc.addEventListener('DOMContentLoaded', function(){
      loadLuxon(function(){
        render();
        setInterval(render, 1000);
        flushReady();
      });
    });
  }
})();
